<template>
  <div v-if="visible" class="fixed inset-0 z-50 bg-white flex flex-col">
    <!-- 模态框内容 - 真正全屏 -->
    <div class="flex-1 flex flex-col h-full overflow-hidden p-6">
      <!-- 标题 -->
      <div class="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 flex-shrink-0">
        <div>
          <h3 class="text-xl font-semibold text-gray-900">
            访问统计
          </h3>
          <div v-if="itemInfo" class="mt-2 flex items-center gap-4 flex-wrap">
            <p class="text-sm text-gray-700">
              <span class="font-medium">站点名称：</span>{{ itemInfo.name || itemInfo.site_name || '-' }}
            </p>
            <p class="text-xs text-gray-500">
              <span class="font-medium">站点ID：</span>{{ itemInfo.id }}
            </p>
            <p class="text-xs text-gray-500">
              <span class="font-medium">UUID：</span>{{ itemInfo.uid || itemInfo.uuid || '-' }}
            </p>
          </div>
        </div>
        <button
          @click="close"
          class="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-all"
          title="关闭"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <!-- 内容 -->
      <div class="flex-1 flex flex-col min-h-0">
            <!-- 加载状态 -->
            <div v-if="loading" class="flex items-center justify-center py-12">
              <div class="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
              <span class="ml-3 text-gray-600">加载中...</span>
            </div>
            
            <!-- 统计摘要 -->
            <div v-else-if="visits.length > 0" class="space-y-4 flex-1 flex flex-col min-h-0">
              <div class="grid grid-cols-3 gap-3 flex-shrink-0">
                <!-- 总访问次数 -->
                <div class="bg-blue-50 rounded-lg p-3 text-center">
                  <div class="text-lg font-semibold text-blue-600">{{ total }}</div>
                  <div class="text-xs text-blue-700">总访问次数</div>
                </div>
                
                <!-- 手机端访问 -->
                <div class="bg-green-50 rounded-lg p-3 text-center">
                  <div class="text-lg font-semibold text-green-600">{{ mobileCount }}</div>
                  <div class="text-xs text-green-700">手机端访问</div>
                </div>
                
                <!-- 电脑端访问 -->
                <div class="bg-purple-50 rounded-lg p-3 text-center">
                  <div class="text-lg font-semibold text-purple-600">{{ desktopCount }}</div>
                  <div class="text-xs text-purple-700">电脑端访问</div>
                </div>
              </div>
              
              <!-- 访问记录列表 -->
              <div class="bg-white border border-gray-200 rounded-lg overflow-hidden flex-1 flex flex-col min-h-0">
                <div class="overflow-y-auto flex-1">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">时间</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP地址</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">地理位置</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">设备/浏览器</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">来源</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                      <tr v-for="visit in visits" :key="visit.id" class="hover:bg-gray-50">
                        <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {{ formatDateTime(visit.visited_at) }}
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-600">
                          {{ visit.ip_address || '-' }}
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-900">
                          <div class="flex items-center">
                            <span class="mr-1">🌍</span>
                            {{ visit.location_display || '-' }}
                          </div>
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-900">
                          <div class="flex items-center">
                            <span class="mr-1">{{ getDeviceIcon(visit.device_type) }}</span>
                            {{ visit.device_display || '-' }}
                          </div>
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" :title="visit.referrer">
                          {{ visit.referrer || '-' }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            <!-- 空状态 -->
            <div v-else class="flex flex-col items-center justify-center py-12 text-gray-500 flex-1">
              <svg class="w-16 h-16 mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p>暂无访问记录</p>
            </div>
      </div>
      
      <!-- 分页控件 -->
      <div v-if="totalPages > 1" class="mt-4 flex items-center justify-center space-x-2 flex-shrink-0">
        <button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          上一页
        </button>
        
        <div class="flex items-center space-x-1">
          <!-- 第一页 -->
          <button
            v-if="currentPage > 3"
            @click="goToPage(1)"
            class="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            1
          </button>
          <span v-if="currentPage > 4" class="px-2 text-gray-500">...</span>
          
          <!-- 当前页附近的页码 -->
          <button
            v-for="page in getPageNumbers()"
            :key="page"
            @click="goToPage(page)"
            :class="[
              'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
              page === currentPage
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
            ]"
          >
            {{ page }}
          </button>
          
          <!-- 最后一页 -->
          <span v-if="currentPage < totalPages - 3" class="px-2 text-gray-500">...</span>
          <button
            v-if="currentPage < totalPages - 2"
            @click="goToPage(totalPages)"
            class="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {{ totalPages }}
          </button>
        </div>
        
        <button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          下一页
        </button>
        
        <span class="ml-2 text-sm text-gray-600">
          第 {{ currentPage }} / {{ totalPages }} 页
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  itemId: {
    type: [String, Number],
    default: null
  },
  itemInfo: {
    type: Object,
    default: null
  },
  apiFunction: {
    type: Function,
    required: true
  }
})

const emit = defineEmits(['update:visible', 'close'])

const loading = ref(false)
const visits = ref([])
const total = ref(0)
const mobileCount = ref(0)
const desktopCount = ref(0)
const currentPage = ref(1)
const pageSize = ref(20)
const totalPages = ref(0)

watch(() => props.visible, async (newVal) => {
  if (newVal && props.itemId) {
    currentPage.value = 1
    await fetchVisits()
  }
})

const fetchVisits = async () => {
  loading.value = true
  try {
    const response = await props.apiFunction(props.itemId, currentPage.value, pageSize.value)
    if (response.success) {
      visits.value = response.data || []
      total.value = response.total || 0
      mobileCount.value = response.mobile_count || 0
      desktopCount.value = response.desktop_count || 0
      totalPages.value = response.total_pages || 0
    }
  } catch (error) {
    console.error('获取访问记录失败:', error)
  } finally {
    loading.value = false
  }
}

const goToPage = async (page) => {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return
  currentPage.value = page
  await fetchVisits()
}

const getPageNumbers = () => {
  const pages = []
  const maxVisible = 5 // 最多显示5个页码
  
  let start = Math.max(1, currentPage.value - 2)
  let end = Math.min(totalPages.value, start + maxVisible - 1)
  
  // 如果末尾不够，调整起始位置
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1)
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
}

const close = () => {
  emit('update:visible', false)
  emit('close')
}

const formatDateTime = (dateTime) => {
  if (!dateTime) return '-'
  const date = new Date(dateTime)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const getDeviceIcon = (deviceType) => {
  const icons = {
    'mobile': '📱',
    'tablet': '📱',
    'desktop': '💻'
  }
  return icons[deviceType] || '💻'
}
</script>

